// src/hooks/useCategories.ts
// -----------------------------------------------------------------------------
// Derives the storefront nav categories LIVE from the tenant's catalogue, via
// smartshop-list-products (decision B / Model A): the distinct `category` values
// across the shop's active products ARE the primary nav. No hardcoded category
// tree - re-categorise a product and the nav follows on next load.
//
// Cached in sessionStorage so nav doesn't refetch on every route change. Order:
// if shopConfig.categories is set, that order wins (pinned); otherwise derived
// alphabetical with the catch-all shelf ("Uncategorized", "Other") pushed to the end.
//
// House rule: short hyphen "-" only.
// -----------------------------------------------------------------------------
import { useEffect, useState } from 'react';
import { shopConfig as cfg } from '../shopConfig';
import { fetchProducts } from '../productsApi';

const CACHE_KEY = `ss_categories_${cfg.gido_id}`;

export function useCategories(): string[] {
  const [cats, setCats] = useState<string[]>(() => {
    if (cfg.categories?.length) return cfg.categories;   // pinned order
    try {
      const cached = sessionStorage.getItem(CACHE_KEY);
      if (cached) return JSON.parse(cached);
    } catch { /* ignore */ }
    return [];
  });

  useEffect(() => {
    if (cfg.categories?.length) return;   // pinned - no fetch needed
    let alive = true;
    (async () => {
      try {
        // view=grid: this hook only needs the distinct `category` values, so the full
        // shape was fetching every description, spec, option and variant - a second
        // ~620 kB request alongside ShopPage's own, for a list of ~29 strings.
        const list = await fetchProducts('grid');
        const distinct = Array.from(new Set(
          list
            .map((p: any) => p.category)
            .filter((c: string) => c && c.trim())
        )) as string[];
        // Alphabetical, with the catch-all shelf last whatever it is called. Matched
        // against a set rather than one literal: the stored value has been
        // "Uncategorized" (American, and probably ours rather than Shopify's), and the
        // moment it is renamed a single hardcoded comparison stops working silently -
        // the shelf just slides into the middle of the nav and nobody notices.
        const CATCH_ALL = new Set(['uncategorized', 'uncategorised', 'other']);
        distinct.sort((a, b) => {
          const ca = CATCH_ALL.has(a.toLowerCase());
          const cb = CATCH_ALL.has(b.toLowerCase());
          if (ca !== cb) return ca ? 1 : -1;
          return a.localeCompare(b);
        });
        if (!alive) return;
        setCats(distinct);
        try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(distinct)); } catch { /* ignore */ }
      } catch { /* keep whatever we have; nav just shows Shop */ }
    })();
    return () => { alive = false; };
  }, []);

  return cats;
}
